import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {useFieldContext} from '../../integrations/form/index.js'
import {Box, Field, HStack, IconButton, Input, InputGroup, InputProps, Stack, StackProps} from '@chakra-ui/react'
import {TFnType, useTranslate} from '@tolgee/react'
import {AnimatePresence, motion} from 'motion/react'
import {forwardRef, useState} from 'react'
import {LuEye, LuEyeClosed} from 'react-icons/lu'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'

interface FieldProps extends InputProps {
  label: string
  required?: boolean
  placeholder?: string
  disabled?: boolean
  helpText?: string
  autoComplete?: string
}

const EyeIcon = motion.create(LuEye)
const EyeClosedIcon = motion.create(LuEyeClosed)

export function PasswordField({required, ...props}: FieldProps) {
  const {t} = useTranslate('forms')
  const field = useFieldContext<string>()

  const [peek, setPeek] = useState(false)

  return (
    <FieldRoot required={required} disabled={props.disabled}>
      <Field.Label>
        {props.label}
        {required && <FieldRequiredIndicator />}
      </Field.Label>

      <InputGroup
        background="bg"
        endElement={
          <IconButton
            tabIndex={-1}
            variant="ghost"
            type="button"
            size="xs"
            me="-2"
            height="calc(100% - {spacing.2})"
            aria-label={t('toggle_password_visibility', 'Toggle password visibility')}
            disabled={props.disabled}
            onClick={e => {
              e.stopPropagation()
              setPeek(old => !old)
            }}
            data-e2e={`button_toggle_${field.name}`}
          >
            <AnimatePresence mode="popLayout">
              {peek ? (
                <EyeIcon
                  layoutId={`${field.name}_eye-icon`}
                  initial={{filter: 'blur(3px)'}}
                  exit={{filter: 'blur(3px)'}}
                  animate={{filter: 'blur(0px)'}}
                />
              ) : (
                <EyeClosedIcon
                  layoutId={`${field.name}_eye-icon`}
                  initial={{filter: 'blur(3px)'}}
                  exit={{filter: 'blur(3px)'}}
                  animate={{filter: 'blur(0px)'}}
                />
              )}
            </AnimatePresence>
          </IconButton>
        }
      >
        <Input
          name={field.name}
          value={field.state.value}
          type={peek ? 'text' : 'password'}
          onChange={e => field.handleChange(e.target.value)}
          onBlur={() => field.handleBlur()}
          {...props}
        />
      </InputGroup>

      {props.helpText && <Field.HelperText>{props.helpText}</Field.HelperText>}
      <ErrorMessage />
    </FieldRoot>
  )
}

interface PasswordStrengthMeterProps extends StackProps {
  max?: number
  value: number
}

/**
 * use https://github.com/dropbox/zxcvbn for strength checking
 */
export const PasswordStrengthMeter = forwardRef<HTMLDivElement, PasswordStrengthMeterProps>(
  function PasswordStrengthMeter(props, ref) {
    const {t} = useTranslate('forms')
    const {max = 4, value, ...rest} = props

    const percent = (value / max) * 100
    const {label, colorPalette} = getColorPalette(percent, t)

    return (
      <Stack align="flex-end" gap="1" ref={ref} {...rest}>
        <HStack width="full" {...rest}>
          {Array.from({length: max}).map((_, index) => (
            <Box
              // oxlint-disable-next-line no-array-index-key
              key={index}
              height="1"
              flex="1"
              rounded="sm"
              data-selected={index < value ? '' : undefined}
              layerStyle="fill.subtle"
              colorPalette="gray"
              _selected={{
                colorPalette,
                layerStyle: 'fill.solid',
              }}
            />
          ))}
        </HStack>
        {label && <HStack textStyle="xs">{label}</HStack>}
      </Stack>
    )
  }
)

function getColorPalette(percent: number, t: TFnType) {
  switch (true) {
    case percent < 33:
      return {label: t('pass_strength_low', 'Low'), colorPalette: 'red'}
    case percent < 66:
      return {label: t('pass_strength_medium', 'Medium'), colorPalette: 'orange'}
    default:
      return {label: t('pass_strength_high', 'High'), colorPalette: 'green'}
  }
}
